import React from 'react';
import { motion } from 'framer-motion';
import { Store, Rocket, Building2, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';

const TargetCustomers = () => {
  const whatsappLink = "https://wa.me/919353316112?text=Hi%20LitheForce!%20I'm%20interested%20in%20your%20AI%20solutions.";
  
  const customers = [
    {
      icon: Store,
      segment: 'Local Businesses',
      industries: 'Restaurants, Gyms, Salons, Clinics',
      challenge: '"I spend 2-3 hours daily answering the same questions on WhatsApp"',
      solution: 'AI chatbot for customer support, order taking, and appointment booking—24/7 automation',
      investment: '₹50k-1L',
      roi: 'Save 15-20 hours/week',
      cta: 'See Restaurant Demo',
      color: 'from-amber-500 to-orange-500',
    },
    {
      icon: Rocket,
      segment: 'Funded Startups',
      industries: 'Seed to Series A Companies',
      challenge: '"We need custom tools but can\'t afford ₹50L agency quotes"',
      solution: 'AI agents, internal dashboards, CRM integrations—built fast and affordable with modern stack',
      investment: '₹2L-10L',
      roi: '5x cheaper than agencies',
      cta: 'View Case Studies',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: Building2,
      segment: 'Enterprises',
      industries: 'Industry 4.0 & Digital Transform.',
      challenge: '"Traditional vendors are slow and expensive, but we need quality"',
      solution: 'Custom AI-powered software, mobile apps, automation pipelines—enterprise-grade at startup speed',
      investment: '₹5L-20L',
      roi: '3-5x faster than legacy firms',
      cta: 'Book Enterprise Call',
      color: 'from-purple-500 to-indigo-500',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  return (
    <section className="section-padding bg-[#f8fafc]" data-testid="target-customers-section">
      <div className="section-container">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12 md:mb-16"
        >
          <span className="text-sm font-medium tracking-wide uppercase text-[#ff6d00] mb-3 block">
            Who We Serve
          </span>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#212121] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="target-customers-headline"
          >
            Who We Work With
          </h2>
          <p className="text-base md:text-lg text-[#5a6b8c] max-w-2xl mx-auto">
            From local shops to funded startups—we serve businesses ready to embrace AI
          </p>
        </motion.div>

        {/* Customer Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-6 md:gap-8"
        >
          {customers.map((customer, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden"
              data-testid={`customer-card-${index}`}
            >
              {/* Header */}
              <div className={`bg-gradient-to-r ${customer.color} p-6 text-white`}>
                <customer.icon className="w-8 h-8 mb-3 opacity-90" />
                <h3
                  className="text-xl font-semibold mb-1"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {customer.segment}
                </h3>
                <p className="text-white/80 text-sm">{customer.industries}</p>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Challenge */}
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-[#5a6b8c] uppercase tracking-wide mb-2">
                    Your Challenge:
                  </h4>
                  <p className="text-[#212121] text-sm italic">{customer.challenge}</p>
                </div>

                {/* Solution */}
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-[#5a6b8c] uppercase tracking-wide mb-2">
                    Our Solution:
                  </h4>
                  <p className="text-[#5a6b8c] text-sm">{customer.solution}</p>
                </div>

                {/* Investment & ROI */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <span className="text-xs text-[#5a6b8c] block">Investment</span>
                    <span className="text-sm font-semibold text-[#1a237e]">{customer.investment}</span>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <span className="text-xs text-[#5a6b8c] block">ROI</span>
                    <span className="text-sm font-semibold text-[#1a237e]">{customer.roi}</span>
                  </div>
                </div>

                {/* CTA */}
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="block">
                  <Button
                    variant="outline"
                    className="w-full btn-secondary flex items-center justify-center gap-2"
                    data-testid={`customer-cta-${index}`}
                  >
                    {customer.cta}
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </a>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default TargetCustomers;
